import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { isBirthCheckOk } from 'validators/Validator';
import { FormErrors } from 'validators/UpdateMemberValidator';
import { callCheckGakkenIdDuplicateApi } from 'requests/RequestUser';
import { MemberForm, MemberUpdateDto } from 'components/templates/MemberUpdateTemplate';
import { formatDate, DATE } from 'common/dateFormatter';
import { RoutePath } from 'consts/RoutePath';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { UpdateGroupMemberReqData, callUpdateGroupMemberApi, UserWithGroupAuth } from 'requests/RequestGroup';
import { HARD_TO_GUESS_PASSWORD } from 'utils/messages';

/**
 * メンバー更新処理のカスタムフック
 * フォームバリデーション -> 更新実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useUpdateMember = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	actionError: string,
	setActionError: React.Dispatch<React.SetStateAction<string>>,
	memberFormData: MemberForm,
	handleCompleteDialogOpen: Function,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
	setPage: React.Dispatch<React.SetStateAction<number>>,
	dto: MemberUpdateDto,
	prevTargetUser: UserWithGroupAuth,
) => {
	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const validate = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, memberFormData[k]) }));
		});

		// パスワードがある場合のみ実行
		if (memberFormData.pass) {
			const hardToGuessPassword = memberFormData.gid.toLowerCase() !== memberFormData.pass.toLowerCase();
			// GIDと同じパスワードを設定できない
			if (!hardToGuessPassword) {
				setFormErrors((prev) => ({
					...prev,
					pass: HARD_TO_GUESS_PASSWORD,
				}));
			}
		}
		// 年月日どれかが入力されている場合、生年月日の正当性チェック
		if (memberFormData.birthYear || memberFormData.birthMonth || memberFormData.birthDate) {
			const isOk = isBirthCheckOk(memberFormData.birthYear, memberFormData.birthMonth, memberFormData.birthDate);
			if (!isOk) {
				setFormErrors((prev) => ({ ...prev, birthYear: '正しい生年月日を入力してください。' }));
			}
		}
		setIsFormValidationFinished(true);
	};

	// 学研ID重複確認
	const history = useHistory();
	const [isGidDuplicateValidationFinished, setIsGidDuplicateValidationFinished] = useState(false);
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const execute = async () => {
			setActionError('');
			if (prevTargetUser.gid !== memberFormData.gid) {
				const checkGidResult = await callCheckGakkenIdDuplicateApi({
					gid: memberFormData.gid,
				});
				if (checkGidResult.status !== HttpStatus.OK) {
					setActionError(ReactError.E9999);
					history.push(RoutePath.ERROR);
					return;
				}
				if (checkGidResult.data.exist) {
					setFormErrors((prev) => ({ ...prev, gid: ReactError.E0005 }));
				}
			}
			setIsGidDuplicateValidationFinished(true);
		};
		execute();
	}, [isFormValidationFinished]);

	useEffect(() => {
		if (!isGidDuplicateValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsGidDuplicateValidationFinished(false);
			setProcessLoading(false);
			return;
		}
		setProcessLoading(false);
		setPage((prev) => prev + 1);
	}, [isGidDuplicateValidationFinished]);

	// 更新実処理
	const [isUpdateFinished, setIsUpdateFinished] = useState(false);
	const update = async () => {
		setProcessLoading(true);
		const isBirthExist = !!memberFormData.birthYear && !!memberFormData.birthMonth && !!memberFormData.birthDate;

		// メンバー情報更新
		const updateGroupMemberReqData: UpdateGroupMemberReqData = {
			gidpk: memberFormData.gidpk,
			gid: memberFormData.gid,
			pass: memberFormData.pass,
			lastname: memberFormData.lastname,
			firstname: memberFormData.firstname,
			kana_lastname: memberFormData.kana_lastname,
			kana_firstname: memberFormData.kana_firstname,
			birth: isBirthExist
				? formatDate(
						new Date(
							Number(memberFormData.birthYear),
							Number(memberFormData.birthMonth) - 1,
							Number(memberFormData.birthDate),
						),
						DATE,
				  )
				: undefined,
			sex: memberFormData.sex,
		};
		const result = await callUpdateGroupMemberApi(dto.groupId, updateGroupMemberReqData);
		if (result.status !== HttpStatus.OK) {
			setIsUpdateFinished(true);
			history.push(RoutePath.ERROR);
			return;
		}
		setIsUpdateFinished(true);
	};

	// ページ遷移
	useEffect(() => {
		if (!isUpdateFinished) {
			return;
		}

		const isError = actionError !== '';
		if (isError) {
			setIsUpdateFinished(false);
			setProcessLoading(false);
			return;
		}

		handleCompleteDialogOpen();
	}, [isUpdateFinished]);

	// 戻る
	const back = async () => {
		setIsFormValidationFinished(false);
		setIsGidDuplicateValidationFinished(false);
		setIsUpdateFinished(false);
		setProcessLoading(false);
		setPage((prev) => prev - 1);
	};

	return {
		validate,
		update,
		back,
	};
};
